/**
 * Уменьшенные кадры карточек маркетплейсов.
 *
 * Зеркала каталогов хранят адрес полноразмерного фото, и экраны показывали
 * именно его: замер 03.09.2026 — 211 673 байта на плитку в 32 пикселя у ОЗОН
 * и 26 720 байт у ВБ, когда в массиве `photos` лежат строки (а лежат они там
 * чаще, чем объекты с ключами размеров).
 *
 * Наш серверный ресайз сюда не достаёт — адреса внешние, `w` понимают только
 * маршруты гейтвея. Зато оба CDN сами отдают готовые размеры, и стоит это
 * одной подменой сегмента пути:
 *
 *   ВБ    .../images/big/1.webp     → .../images/c246x328/1.webp  (26,7 КБ → 5,7 КБ)
 *   ОЗОН  /s3/multimedia-1-0/x.jpg  → /s3/multimedia-1-0/wc250/x.jpg (211 КБ → 16 КБ)
 *
 * Адрес чужого хоста возвращается без изменений: подменять путь можно только
 * там, где размерная схема известна наверняка.
 */

/** Ширины кадров ВБ по возрастанию: сегмент пути → фактическая ширина. */
const WB_VARIANTS: Array<{ segment: string; width: number }> = [
  { segment: 'tm', width: 75 },
  { segment: 'c246x328', width: 246 },
  { segment: 'c516x688', width: 516 },
  { segment: 'big', width: 900 },
]

/** Размеры, которые отдаёт CDN ОЗОН по префиксу `wc<N>`. */
const OZON_WIDTHS = [50, 100, 200, 250, 500, 700, 1000]

/**
 * Размеры кадра Я.Маркета (avatars.mds.yandex.net): последний сегмент пути —
 * это размер, `orig` — полноразмерный кадр. Список размеров и сам факт, что
 * последний сегмент всегда размер, — по документации Маркета для картинок
 * avatars.mds.yandex.net («get-marketpic»); НЕ ПРОВЕРЕНО на живых данных —
 * первая синхронизация каталога подтвердит формат `main_picture`/`pictures`.
 * Если предположение не оправдается, `isYandexMarketPhoto` не совпадёт и
 * функция вернёт исходный адрес (обязательный фолбэк ниже).
 */
const YM_WIDTHS: Array<{ segment: string; width: number }> = [
  { segment: '80x80', width: 80 },
  { segment: '200x200', width: 200 },
  { segment: '450x450', width: 450 },
]

/** Наименьший доступный размер, который не меньше запрошенного. */
function fitWidth(available: number[], width: number): number | null {
  return available.find((value) => value >= width) ?? null
}

function isWildberriesPhoto(url: URL): boolean {
  return /(^|\.)wbbasket\.ru$/.test(url.hostname) && url.pathname.includes('/images/')
}

function isOzonPhoto(url: URL): boolean {
  return /(^|\.)ozone\.ru$/.test(url.hostname) && url.pathname.startsWith('/s3/')
}

function isYandexMarketPhoto(url: URL): boolean {
  return /(^|\.)avatars\.mds\.yandex\.net$/.test(url.hostname) && url.pathname.includes('/get-marketpic/')
}

function wildberriesThumbnail(url: URL, width: number): string {
  const target = WB_VARIANTS.find((variant) => variant.width >= width) ?? WB_VARIANTS[WB_VARIANTS.length - 1]
  const segments = url.pathname.split('/')
  const imagesAt = segments.lastIndexOf('images')
  // Размер — сегмент сразу после `images`, а за ним имя файла. Другой формы у
  // адресов корзины не встречается, но проверка дешевле разбора битой ссылки.
  if (imagesAt < 0 || imagesAt + 2 >= segments.length) return url.toString()
  const current = segments[imagesAt + 1]
  if (!WB_VARIANTS.some((variant) => variant.segment === current)) return url.toString()
  if (current === target.segment) return url.toString()
  segments[imagesAt + 1] = target.segment
  url.pathname = segments.join('/')
  return url.toString()
}

function ozonThumbnail(url: URL, width: number): string {
  const target = fitWidth(OZON_WIDTHS, width)
  if (target === null) return url.toString()
  const segments = url.pathname.split('/')
  const fileAt = segments.length - 1
  if (fileAt < 1) return url.toString()
  // Повторный вызов не должен наращивать префиксы: свой `wc<N>` заменяем.
  if (/^wc\d+$/.test(segments[fileAt - 1])) {
    segments[fileAt - 1] = `wc${target}`
  } else {
    segments.splice(fileAt, 0, `wc${target}`)
  }
  url.pathname = segments.join('/')
  return url.toString()
}

/** Последний сегмент пути — размер (`orig`, либо уже подставленный `NxN`). */
function yandexMarketThumbnail(url: URL, width: number): string {
  const target = YM_WIDTHS.find((variant) => variant.width >= width) ?? YM_WIDTHS[YM_WIDTHS.length - 1]
  const segments = url.pathname.split('/')
  const lastAt = segments.length - 1
  if (lastAt < 1 || !segments[lastAt]) return url.toString()
  if (segments[lastAt] === target.segment) return url.toString()
  segments[lastAt] = target.segment
  url.pathname = segments.join('/')
  return url.toString()
}

/**
 * Адрес кадра карточки под нужную ширину показа.
 *
 * Ширину задавайте в физических пикселях (CSS-размер × плотность экрана):
 * плитке таблицы в 32 пикселя хватает 96, режиму «Фото» — 250.
 */
export function marketplaceThumbnailUrl(source: string, width?: number): string {
  if (!source || !width || !Number.isFinite(width) || width <= 0) return source
  let url: URL
  try {
    url = new URL(source)
  } catch {
    return source
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return source
  if (isWildberriesPhoto(url)) return wildberriesThumbnail(url, width)
  if (isOzonPhoto(url)) return ozonThumbnail(url, width)
  if (isYandexMarketPhoto(url)) return yandexMarketThumbnail(url, width)
  return source
}
